import React, { useEffect, useState } from "react";
import { BASE_FUN_URL, BASE_URL, mapConfig } from "./constant";
import { HubConnectionBuilder } from "@microsoft/signalr";
import axios from "axios";
import { useJsApiLoader } from "@react-google-maps/api";
import { gapi } from "gapi-script";
import { jwtDecode } from "jwt-decode";

const AppContext = React.createContext({
  token: "",
  isLoggedIn: false,
  login: (profile) => {},
  logout: () => {},
});

const navLinks = [
  {
    name: "Dashboard",
    href: "/dashboard",
    soon: false,
    current: true,
  },
  {
    name: "Home",
    href: "/home",
    soon: false,
    current: true,
  },
  {
    name: "Analytics",
    href: "/analytics",
    soon: false,
    current: false,
  },
  {
    name: "Health",
    href: "/health",
    soon: false,
    current: false,
  },
];

export const AppContextProvider = (props) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: mapConfig.googleMapsApiKey,
    libraries: mapConfig.libraries,
  });
  const [timeZone, setTimeZone] = useState();
  const profileString = localStorage.getItem("rms_profile");
  let initialProfile = null;
  if (profileString) initialProfile = JSON.parse(profileString);
  const [profile, setProfile] = useState(initialProfile);
  const [playbackMode, setPlayBackMode] = useState(100);
  const [alarm, setAlarm] = useState([]);
  const [data, setData] = useState({});
  const [updatedAlarm, setUpdatedAlarm] = useState({});
  const [teams, setTeams] = useState(null);
  const [config, setConfig] = useState(null);
  // const [allAssets, setAllAssets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [navigation, setNavigation] = useState(navLinks);
  const [darkMode, setDarkMode] = useState(true);
  const [parentAssetLoc, setParentAssetLoc] = useState();

  const userIsLoggedIn = !!profile?.token;
  if (userIsLoggedIn) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${profile.token}`;
  }
  const getTimeZoneDateTime = (timeStamp) => {
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formattedDate = new Date(timeStamp).toLocaleString("en-US", {
      timeZone: config?.timeZone || currentTimeZone,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    return new Date(formattedDate);
  };

  const loginHandler = (userProfile) => {
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${userProfile.token}`;
    localStorage.setItem("rms_profile", JSON.stringify(userProfile));
    setProfile(userProfile);
  };

  const logoutHandler = () => {
    handleGoogleLogout();
    setProfile(null);
    setConfig(null);
    setAssets([]);
    localStorage.removeItem("rms_profile");
  };

  const authCtx = {
    profile: profile,
    isLoading: loading,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
  };

  function handleGoogleLogout() {
    gapi.auth2
      .getAuthInstance()
      ?.signOut()
      .then(() => {
        console.log("User signed out successfully.");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  }

  const getUser = async (email) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/Auth/SingleSignOn?email=${email}`
      );
      if (res.data.active) {
        loginHandler(res.data);
        setCookie("GD-RMS-Profile", JSON.stringify(res.data.tokenId), 7);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkToken = async () => {
    try {
      let token = gapi.auth?.getToken();
      let userProfile = getCookie("GD-RMS-Profile");
      if (!profile?.token && token && token.id_token && token.access_token) {
        const accessToken = token.id_token;

        const decodedToken = jwtDecode(accessToken);
        if (decodedToken.email) {
          getUser(decodedToken.email);
        }
      } else if (!profile?.token && userProfile) {
        const decodedToken = jwtDecode(userProfile);
        if (decodedToken.email) {
          await getUser(decodedToken.email);
        }
      }
    } catch (error) {
      console.error("Error checking token:", error);
    }
  };

  function setCookie(name, value, days) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }
  const getCookie = (name) => {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }
    return null;
  };

  const fetchAssetDatas = async (userConfigData) => {
    const teamIds = profile.teamsIds ? profile.teamsIds.split(",") : [];
    const companyId = profile.companyId;
    let res = await axios.post(
      `${BASE_URL}/api/TeamMaster/GetByIds?companyId=${companyId}`,
      teamIds
    );
    const teamPayload = {
      companyId: authCtx.profile.companyId,
      teamAssets: res.data.map((x) => ({
        ...x,
        assetTypeIds: x.assetTypes?.map((x) => x.id)?.join(",") || "",
        assetTypes: x.assetTypes.map((x) => ({
          ...x,
          assetIds: x?.assetIds ?? "",
        })),
      })),
    };
    setTeams(res.data);
    setConfig({
      ...userConfigData,
      homeConfiguration: userConfigData?.homeConfiguration,
    });
    if (teamPayload && teamPayload.teamAssets.length > 0) {
      const assetResponse = await axios.post(
        `${BASE_URL}/api/Asset/GetByActiveFlag`,
        teamPayload
      );
      if (assetResponse.status === 200) {
        const resData = assetResponse.data.map((x) => ({
          ...x,
          parameters: x.parameters.map((y) => {
            const rgbValues = y.cardBgColor?.match(/\d+/g).map(Number);

            // Add the alpha value
            const rgba = rgbValues
              ? `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, 0.1)`
              : null;
            return {
              ...y,
              cardBgColor: rgba,
            };
          }),
        }));
        setAssets(resData);
        if (resData.length > 0) {
          const assetAlarmRes = await axios.post(
            `${BASE_URL}/api/Asset/GetAllAssetAlarm`,
            teamPayload
          );
          let updatedResData = resData.map((uad) => {
            const latestAlarm = assetAlarmRes.data.find(
              (aad) => aad.assetId === uad.id
            );
            return { ...uad, latestAlarm: latestAlarm?.dataAlarm };
          });
          setAssets(updatedResData);
          const locationData = await axios.post(
            `${BASE_URL}/api/Asset/GetAllAssetLocation`,
            teamPayload
          );
          updatedResData = updatedResData.map((rs) => {
            if (!rs.parentId) {
              const checkAsset = locationData.data.find(
                (ld) =>
                  ld.assetId === rs.id &&
                  rs.parameters.some((p) => p.deviceId === ld.deviceId)
              );
              return checkAsset !== undefined
                ? {
                    ...rs,
                    gnss: {
                      latitude: parseFloat(checkAsset.gnss.latitude),
                      longitude: parseFloat(checkAsset.gnss.longitude),
                    },
                    timeStamp: checkAsset.timeStamp,
                  }
                : rs;
            } else {
              const parentAsset = locationData.data.find(
                (ld) => ld.assetId === rs.parentId
              );
              return parentAsset !== undefined
                ? {
                    ...rs,
                    gnss: {
                      latitude: parseFloat(parentAsset.gnss.latitude),
                      longitude: parseFloat(parentAsset.gnss.longitude),
                    },
                  }
                : rs;
            }
          });
          setAssets(updatedResData);
          const parentAssetLocations = locationData.data.filter(
            (x) => x.isParent
          );
          if (parentAssetLocations) setParentAssetLoc(parentAssetLocations);
          const assetDataRes = await axios.post(
            `${BASE_URL}/api/Asset/GetAllAssetData`,
            teamPayload
          );

          const sortedData = assetDataRes.data.sort((a, b) => {
            const timestampA = new Date(a.timeStamp);
            const timestampB = new Date(b.timeStamp);
            return timestampB - timestampA; // Compare in reverse order
          });
          updatedResData = updatedResData.map((rs) => ({
            ...rs,
            parameters: rs.parameters.map((p) => {
              const latestData = assetDataRes.data.find(
                (a) =>
                  a.deviceId === p.deviceId &&
                  a.parameterId === p.parameterId &&
                  a.assetId === rs.id
              );
              return latestData !== undefined
                ? {
                    ...p,
                    value: latestData.value,
                    timeStamp: latestData.timeStamp,
                  }
                : p;
            }),
            timeStamp:
              rs.devices.length > 0 && sortedData.length > 0
                ? sortedData.filter(
                    (x) =>
                      x.deviceId === rs.devices[0].device.id &&
                      x.assetId === rs.id
                  )[0]?.timeStamp
                : null,
          }));
          setAssets(updatedResData);
          // setAllAssets(updatedResData);
        }
      } else {
        setApiLoaded(true);
        setAssets([]);
      }
    } else {
      setAssets([]);
      setApiLoaded(true);
    }
  };

  const fetchData = async (currentTimeZone) => {
    if (profile) {
      try {
        const companyId = profile?.companyId;
        const assetHealth = await axios.get(
          `${BASE_URL}/api/AssetHealth/GetFullAssetHealth?companyId=${companyId}`
        );
        if (assetHealth.status === 200) {
          setNavigation((pre) =>
            pre.map((x) =>
              x.name === "Health"
                ? {
                    ...x,
                    showStatus: assetHealth.data > 0,
                    badAssetCount: assetHealth.data,
                  }
                : x
            )
          );
        }
        setConfig(null);
        setAssets(null);
        let url = null;
        if (profile.role === 100) {
          if (companyId)
            url = `${BASE_URL}/api/UserConfiguration/GetForSuperAdmin?userId=${profile?.id}&companyId=${companyId}`;
          else setApiLoaded(false);
        } else url = `${BASE_URL}/api/UserConfiguration/${profile.id}`;
        if (url) {
          const userConfigResponse = await axios.get(url);
          if (userConfigResponse.status === 200) {
            const userConfigData = userConfigResponse.data;
            setConfig({
              ...userConfigData,
              timeZone: userConfigData.timeZone || currentTimeZone,
            });
            if (userConfigData?.homeConfiguration?.length > 0) {
              await fetchAssetDatas(userConfigData);
            } else {
              setAssets(null);
              setApiLoaded(true);
            }
          } else {
            setAssets(null);
            setApiLoaded(true);
            setConfig(null);
          }
        } else {
          setAssets(null);
          setApiLoaded(true);
          setConfig(null);
        }
      } catch (error) {
        setAssets(null);
        setApiLoaded(true);
        setConfig(null);
        console.error("Error fetching data:", error);
      }
    }
  };

  useEffect(() => {
    document.documentElement.classList.add("dark");
    if (profile !== null) {
      const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      fetchData(currentTimeZone);
    }
  }, [profile]);

  useEffect(() => {
    checkToken();
  }, []);

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(`${BASE_FUN_URL}/api`)
      .build();
    connection.on("rmsRecieved", (data) => {
      // Update the relevant asset's parameter value in the assets state
      if (data) {
        setData(data);
        setAssets((prevAssets) => {
          if (prevAssets)
            return prevAssets.map((asset) => {
              // Check if the asset has the parameter with the matching deviceId and sensorId
              const updatedParameters = asset.parameters.map((param) => {
                if (param.deviceId === data?.deviceId) {
                  const value = data.Values.find(
                    (v) =>
                      asset.id === v.AssetId &&
                      v.ParameterId === param.parameterId &&
                      param.sensorId === v.Id
                  );
                  if (value)
                    return {
                      ...param,
                      value: parseFloat(value.Value),
                      timeStamp: data.timeStamp,
                    };
                }
                return param;
              });
              if (
                asset.devices.length > 0 &&
                asset.devices[0].device.id === data.deviceId
              ) {
                const gnssValue = {
                  latitude: data.gnss.Latitude,
                  longitude: data.gnss.Longitude,
                };
                // Return the updated asset with the updated parameters
                return {
                  ...asset,
                  gnss:
                    data.gnss &&
                    data.gnss.Latitude !== 0 &&
                    data.gnss.Longitude !== 0
                      ? gnssValue
                      : asset.gnss,
                  timeStamp: data.timeStamp,
                  parameters: updatedParameters,
                };
              }
              return {
                ...asset,
                timeStamp:
                  asset.devices.length > 0 &&
                  asset.devices[0].device.id === data.deviceId
                    ? data.timeStamp
                    : asset.timeStamp,
                parameters: updatedParameters,
              };
            });
        });
      }
    });

    connection.on("alarmRecieved", (data) => {
      setAlarm(data);
    });
    connection.on("alarmUpdated", (data) => {
      setUpdatedAlarm(data);
    });
    connection
      .start()
      .catch((error) =>
        console.error("Error starting SignalR connection:", error)
      );

    return () => {
      connection
        .stop()
        .catch((error) =>
          console.error("Error stopping SignalR connection:", error)
        );
    };
  }, []);

  useEffect(() => {
    const refreshInterval = 20 * 60 * 1000; // 20 minutes in milliseconds

    const refreshPage = () => {
      setPlayBackMode((pre) => {
        if (
          pre !== 200 &&
          navigation.find((x) => x.current === true)?.name === "Dashboard"
        ) {
          window.location.reload();
        }
        return pre;
      });
    };

    const refreshTimer = setTimeout(refreshPage, refreshInterval);

    return () => {
      clearTimeout(refreshTimer);
    };
  }, []);

  return (
    <AppContext.Provider
      value={{
        authCtx,
        alarm,
        isLoaded,
        setAlarm,
        data,
        setData,
        config,
        setConfig,
        assets,
        setAssets,
        apiLoaded,
        setApiLoaded,
        updatedAlarm,
        playbackMode,
        setPlayBackMode,
        navigation,
        setNavigation,
        timeZone,
        setTimeZone,
        getTimeZoneDateTime,
        darkMode,
        setDarkMode,
        parentAssetLoc,
        teams,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};

export default AppContext;
